import { LOYALTY_API_BASE } from "../constants";
import { loyaltyFetch } from "../hooks/loyaltyFetch";
import { loyaltyApiDetail } from "./loyaltyApiMessage";
import { loyaltyBrandHeaders, normalizeLoyaltyBrand } from "./loyaltyBrand";

export { loyaltyBrandHeaders, normalizeLoyaltyBrand } from "./loyaltyBrand";

/** Ajoute ?email= si connu (réconciliation post-fusion Unomi). */
export function withEmailQuery(url, email) {
    const normalized = String(email || "").trim();
    if (!normalized) return url;
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}email=${encodeURIComponent(normalized)}`;
}

export function extractProfileIdFromLoyaltyResponse(data, fallback = "") {
    return String(data?.profileId || data?.profile_id || fallback || "").trim();
}

/**
 * Email pour réconciliation Loyalty (brand + email).
 * Priorité : properties.email, puis scopeEmail (avec retrait du préfixe marque).
 */
export function resolveContactEmailForLoyalty(contact, brandName = "") {
    const props = contact?.properties || contact || {};
    const direct = String(props.email || "").trim();
    if (direct) return direct;

    const scope = String(props.scopeEmail || props.scopeemail || "").trim();
    if (!scope) return "";

    const brand = normalizeLoyaltyBrand(brandName);
    if (brand) {
        const prefix = `${brand}-`;
        if (scope.toLowerCase().startsWith(prefix)) {
            return scope.slice(prefix.length);
        }
    }
    return scope;
}

export function buildCustomerPath(brandName, profileId, suffix = "", email = "") {
    const brand = normalizeLoyaltyBrand(brandName);
    const base = `${LOYALTY_API_BASE}/customers/${encodeURIComponent(brand)}/${encodeURIComponent(profileId)}${suffix}`;
    return withEmailQuery(base, email);
}

export function buildTransactionsByUserUrl({
    brandName,
    profileId,
    email = "",
    transactionType = "",
} = {}) {
    const brand = normalizeLoyaltyBrand(brandName);
    const params = new URLSearchParams({
        brand,
        profileId: String(profileId),
    });
    if (transactionType) params.set("transactionType", transactionType);
    const base = transactionType
        ? `${LOYALTY_API_BASE}/transactions/by-user-and-type?${params}`
        : `${LOYALTY_API_BASE}/transactions/by-user?${params}`;
    return withEmailQuery(base, email);
}

async function parseLoyaltyError(res) {
    const data = await res.json().catch(() => null);
    return loyaltyApiDetail(data?.detail) || `Erreur HTTP ${res.status}`;
}

export async function fetchLoyaltyCustomerRecord(brandName, profileId, email = "") {
    const brand = normalizeLoyaltyBrand(brandName);
    const headers = loyaltyBrandHeaders(brand);
    const res = await loyaltyFetch(
        buildCustomerPath(brand, profileId, "", email),
        { headers },
    );
    if (!res.ok) return { ok: false, status: res.status, customer: null, error: await parseLoyaltyError(res) };
    const customer = await res.json();
    return {
        ok: true,
        status: res.status,
        customer,
        profileId: extractProfileIdFromLoyaltyResponse(customer, profileId),
    };
}

export async function fetchLoyaltySummary(brandName, profileId, email = "") {
    const brand = normalizeLoyaltyBrand(brandName);
    const res = await loyaltyFetch(
        buildCustomerPath(brand, profileId, "/loyalty", email),
        { headers: loyaltyBrandHeaders(brand) },
    );
    if (!res.ok) return null;
    return res.json();
}

/**
 * Chargement fidélité client — ordre backend :
 * 1) /loyalty?email= (alias + profileId maître)
 * 2) fiche, mouvements, coupons en parallèle
 */
export async function fetchCustomerLoyaltyBundle(brandName, entryProfileId, email = "") {
    const brand = normalizeLoyaltyBrand(brandName);
    const emailNorm = String(email || "").trim();
    const entry = String(entryProfileId || "").trim();
    const headers = loyaltyBrandHeaders(brand);

    if (!brand || !entry) {
        return { ok: false, fatalError: "Marque et profileId requis." };
    }

    const loyaltyRes = await loyaltyFetch(
        buildCustomerPath(brand, entry, "/loyalty", emailNorm),
        { headers },
    );
    if (!loyaltyRes.ok) {
        return {
            ok: false,
            status: loyaltyRes.status,
            fatalError: await parseLoyaltyError(loyaltyRes),
        };
    }

    const loyalty = await loyaltyRes.json();
    const activeProfileId = extractProfileIdFromLoyaltyResponse(loyalty, entry);
    const errors = {};
    if (!emailNorm) {
        errors.emailMissing = "Ajoutez l’email client (?email=…) pour un historique complet après fusion Unomi.";
    }

    const [customerRes, pmRes, cwrRes] = await Promise.all([
        loyaltyFetch(buildCustomerPath(brand, activeProfileId, "", emailNorm), { headers }),
        loyaltyFetch(buildCustomerPath(brand, activeProfileId, "/point-movements", emailNorm), { headers }),
        loyaltyFetch(buildCustomerPath(brand, activeProfileId, "/coupons-with-rewards", emailNorm), { headers }),
    ]);

    let customer = null;
    if (customerRes.ok) {
        customer = await customerRes.json();
    } else {
        errors.customer = await parseLoyaltyError(customerRes);
    }

    let movements = [];
    if (pmRes.ok) {
        const raw = await pmRes.json().catch(() => []);
        movements = Array.isArray(raw) ? raw : [];
    } else {
        errors.pointMovements = await parseLoyaltyError(pmRes);
    }

    let couponsWithRewards = [];
    if (cwrRes.ok) {
        const cwrData = await cwrRes.json().catch(() => null);
        couponsWithRewards = Array.isArray(cwrData?.items) ? cwrData.items : [];
    } else {
        errors.coupons = await parseLoyaltyError(cwrRes);
    }

    return {
        ok: true,
        brand,
        loyalty,
        customer,
        activeProfileId,
        movements,
        couponsWithRewards,
        errors,
        email: emailNorm,
    };
}
